import React, { useState, useEffect, FormEvent } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment';
import Modal from '@src/components/modal/Modal';
import styles from './SentryTrackingModal.module.scss';
import { useSentry } from '../../hooks/useSentry';
import { TextInput } from '@src/components/text-input/TextInput';
import Button from '@src/components/button/Button';
import Select from '@src/components/select/Select';
import Checkbox from '@src/components/checkbox/Checkbox';
import { useToast } from '@src/hooks/useToast';
import { logger } from '../../utils/logger';
import { 
  ADD_SENTRY_TRACKING, 
  REMOVE_SENTRY_TRACKING, 
  GET_ENTITY_SENTRY_TRACKING 
} from './sentryGraphQL';

// Props for the SentryTrackingModal component
interface SentryTrackingModalProps {
  visible: boolean;
  onClose: () => void;
  entityId: string;
  entityType: 'user' | 'account' | 'school';
  entityName: string;
  onSuccess?: () => void;
}

/**
 * A flexible modal component for managing Sentry tracking for entities
 */
const SentryTrackingModal: React.FC<SentryTrackingModalProps> = ({
  visible,
  onClose,
  entityId,
  entityType,
  entityName,
  onSuccess
}) => {
  const { refreshTracking } = useSentry();
  const { successToast, errorToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [existingTracking, setExistingTracking] = useState<any>(null);
  
  // Form state
  const [trackingLevel, setTrackingLevel] = useState('basic');
  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Mutation for adding Sentry tracking
  const [addSentryTracking] = useMutation(ADD_SENTRY_TRACKING, {
    onCompleted: (data) => {
      if (data?.addSentryTracking) {
        successToast(`Sentry tracking ${existingTracking ? 'updated' : 'enabled'} for ${entityType}: ${entityName}`);
        
        // Refresh tracking data
        refreshTracking();
        
        if (onSuccess) onSuccess();
        onClose();
      } else {
        errorToast(`Failed to ${existingTracking ? 'update' : 'enable'} Sentry tracking: No data returned`);
      }
      setLoading(false);
    },
    onError: (error) => {
      errorToast(`Failed to ${existingTracking ? 'update' : 'enable'} Sentry tracking: ${error.message}`);
      setLoading(false);
    }
  });

  // Mutation for removing Sentry tracking
  const [removeSentryTracking] = useMutation(REMOVE_SENTRY_TRACKING, {
    onCompleted: (data) => {
      if (data?.removeSentryTracking) {
        successToast(`Sentry tracking disabled for ${entityType}: ${entityName}`);
        
        // Refresh tracking data
        refreshTracking();
        
        if (onSuccess) onSuccess();
        onClose();
      } else {
        errorToast('Failed to disable Sentry tracking: No data returned');
      }
      setLoading(false);
    },
    onError: (error) => {
      errorToast(`Failed to disable Sentry tracking: ${error.message}`);
      setLoading(false);
    }
  });

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await addSentryTracking({
        variables: {
          input: {
            entityId,
            entityType,
            trackingLevel,
            startDate,
            endDate: endDate || null,
            isActive
          }
        }
      });
    } catch (error) {
      // Error is handled in the onError callback
      logger.error('Error in handleSubmit:', error);
      setLoading(false);
    }
  };

  // Handle disabling tracking
  const handleDisable = async () => {
    if (!existingTracking?.id) {
      errorToast('No tracking ID found');
      return;
    }
    
    setLoading(true);
    
    try {
      await removeSentryTracking({
        variables: {
          id: existingTracking.id
        }
      });
    } catch (error) {
      // Error is handled in the onError callback
      logger.error('Error in handleDisable:', error);
      setLoading(false);
    }
  };

  const trackingLevelOptions = [
    { value: 'basic', label: 'Basic (Errors only)' },
    { value: 'detailed', label: 'Detailed (Errors and warnings)' },
    { value: 'full', label: 'Full (All logs)' }
  ];

  return (
    <Modal open={visible} onClose={onClose}>
      <div className={styles.title}>
        {`${existingTracking ? 'Update' : 'Enable'} Sentry Tracking`}
      </div>
      
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.content}>
          <div className={styles.description}>
            {existingTracking 
              ? `Update error tracking and monitoring for ${entityType}:`
              : `Enable error tracking and monitoring for ${entityType}:`
            }
          </div>
          
          <div className={styles.entityDetails}>
            {entityType === 'user' && entityName.includes('\n') ? (
              // For users, display structured information
              entityName.split('\n').map((line, index) => (
                <div key={index} className={index === 0 ? styles.entityName : styles.entityInfo}>
                  {line}
                </div>
              ))
            ) : (
              // For other entity types, display as before
              <div className={styles.entityName}>{entityName}</div>
            )}
          </div>
          
          <Select
            inline={true}
            label="Tracking Level"
            value={trackingLevel}
            onChange={(option) => setTrackingLevel(option.value)}
            options={trackingLevelOptions}
            placeholder="Select tracking level"
          />
          
          <TextInput
            inline={true}
            label="Start Date"
            id="startDate"
            type="date"
            value={startDate}
            onChange={(value) => setStartDate(value as string)}
          />
          
          <TextInput
            inline={true}
            label="End Date (Optional)"
            id="endDate"
            type="date"
            value={endDate}
            onChange={(value) => setEndDate(value as string)}
          />
          
          <div className={styles.row}>
            <div className={styles.label}>Active</div>
            <Checkbox
              id="isActive"
              checked={isActive}
              onChange={(value) => setIsActive(value)}
              label=""
            />
          </div>
          
          <div className={styles.action}>
            <div />
            <div className={styles.buttonGroup}>
              <Button
                disabled={loading}
                type="submit"
                label={existingTracking ? 'Update Tracking' : 'Enable Tracking'}
                size="normal"
              />
              
              {existingTracking && (
                <Button
                  color="red"
                  onClick={handleDisable}
                  disabled={loading}
                  label="Disable Tracking"
                  size="normal"
                />
              )}
              
              <Button
                outline
                onClick={onClose}
                label="Cancel"
                size="normal"
              />
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default SentryTrackingModal;
